import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { useVirtualList } from "@certa/catalyst/hooks/useVirtualList";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { classNames } from "@certa/catalyst/utils/common";
import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import React from "react";
import { useIntl } from "react-intl";
import {
  SEARCH_LISTBOX_ID,
  type SearchResultType
} from "../GlobalSearchWithSDO.constants";
import { useGlobalSearch } from "../hooks/useGlobalSearch";
import { useSearchStore } from "../store/useSearchStore";
import { mainContentStyles as styles } from "./AllResults.styles";
import {
  ResultItem,
  SearchResultItemSkeleton,
  SearchResultsLoading
} from "./ResultItem";
import { SkeletonGroup } from "@certa/catalyst/components/Skeleton";
import type { ListInteractionMode } from "@certa/catalyst/hooks/useListInteraction";
import {
  Button,
  ButtonSizes,
  ButtonVariants
} from "@certa/catalyst/components/Button";
import { ChevronLeft } from "@certa/icons/components/ChevronLeft";
import { useGetSearchResultsLabelByType } from "../hooks/useGetSearchResultsLabelByType";

const ITEM_HEIGHT = 57;

type MainContentProps = {
  onResultClick: (
    result: ModelledAPIResponse.SearchResult,
    index: number,
    interactionMode: ListInteractionMode,
    query: string
  ) => void;
  selectedResultType: SearchResultType;
};

export const createSearchResultId = (index: number) => `search-result-${index}`;

export const AllResults: React.FC<MainContentProps> = ({
  selectedResultType,
  onResultClick
}) => {
  const {
    selectedItem,
    setSelectedItem,
    interactionMode,
    setInteractionMode,
    setSelectedResultType,
    selectedSearchTypeForAdvancedFilters,
    query
  } = useSearchStore();
  const intl = useIntl();

  const searchResults = useGlobalSearch();
  const { SEARCH_RESULT_TYPE_LABELS } = useGetSearchResultsLabelByType();

  const {
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    flatData,
    status
  } = searchResults[selectedResultType];

  const {
    getListboxProps,
    getViewportProps,
    getOffsetterProps,
    getRowProps,
    state: { list }
  } = useVirtualList({
    totalItemsCount: flatData.length,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: fetchNextPage,

    isCombobox: false,
    activeIndex: selectedItem,
    selectedIndex: selectedItem,
    setActiveIndex: setSelectedItem,
    interactionMode,
    setInteractionMode,
    rowHeight: ITEM_HEIGHT,
    overscan: 30,
    onSelect: (index: number, interactionMode: ListInteractionMode) => {
      onResultClick(flatData[index], index, interactionMode, query);
    },
    getOptionId: createSearchResultId
  });

  const listBoxProps = getListboxProps({ label: "Search results" });
  const viewportProps = getViewportProps();
  const offsetterProps = getOffsetterProps();

  const noResultsMessage = intl.formatMessage({
    id: "globalSearch.noResults",
    defaultMessage: "No results found."
  });

  const trySearchingMessage = intl.formatMessage({
    id: "globalSearch.trySearching",
    defaultMessage: "Try different keywords or filters."
  });

  const allTypesLabel = intl.formatMessage({
    id: "globalSearch.allTypes",
    defaultMessage: "All Types"
  });

  const handleShowAllTypes = () => {
    setSelectedResultType(null);
  };

  if (status === "loading" || isLoading) {
    return (
      <div
        className={classNames(styles.container, styles.loadingLayout)}
        data-testid="search-loading-skeleton"
        ref={listBoxProps.ref}
      >
        <SkeletonGroup label="Loading next page">
          <SearchResultsLoading />
        </SkeletonGroup>
      </div>
    );
  }

  const noResults = (
    <Stack
      height="100%"
      justify="center"
      align="center"
      width="100%"
      ref={listBoxProps.ref}
    >
      <Typography
        variant={TypographyVariants.BODY}
        color={TypographyColors.NEUTRAL_700}
      >
        {noResultsMessage}
      </Typography>
      <Typography
        variant={TypographyVariants.BODY}
        color={TypographyColors.NEUTRAL_700}
      >
        {trySearchingMessage}
      </Typography>
    </Stack>
  );

  return (
    <div className={classNames(styles.container, listBoxProps.className)}>
      {selectedSearchTypeForAdvancedFilters ? null : (
        <div
          className={classNames(
            styles.paddingX,
            styles.paddingY,
            styles.buttonContainer
          )}
        >
          <Button
            leftIcon={<ChevronLeft />}
            variant={ButtonVariants.TEXT}
            size={ButtonSizes.SMALL}
            onClick={handleShowAllTypes}
          >
            {allTypesLabel}
          </Button>
          <Typography
            variant={TypographyVariants.LABEL_SM}
            color={TypographyColors.NEUTRAL_700}
            className={classNames(styles.borderBottom, styles.paddingY)}
          >
            {SEARCH_RESULT_TYPE_LABELS[selectedResultType]}
          </Typography>
        </div>
      )}
      {flatData.length > 0 ? (
        <div
          {...listBoxProps}
          id={SEARCH_LISTBOX_ID}
          className={classNames(
            styles.listContainer,
            listBoxProps.className,
            styles.paddingX,
            "scrollbar-on-hover"
          )}
        >
          <div {...viewportProps}>
            <div {...offsetterProps}>
              {list.map(row => {
                const { rowProps, optionProps, rowKey, isLoaderRow } =
                  getRowProps(row);
                const rowData = flatData[row.index];
                const isSelected = row.index === selectedItem;

                return (
                  <div key={rowKey} {...rowProps}>
                    {isLoaderRow ? (
                      hasNextPage ? (
                        <SkeletonGroup label="Loading next page">
                          <SearchResultItemSkeleton />
                        </SkeletonGroup>
                      ) : null
                    ) : (
                      <div {...optionProps} className={styles.option}>
                        <ResultItem item={rowData} isSelected={isSelected} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        noResults
      )}
    </div>
  );
};
